import {Injectable} from '@angular/core';
import {
  createDefaultFacet,
  createDefaultSpecification,
  createDefaultValueField,
  IDSFacet as UIFacet,
  IDSInfo as UIInfo,
  IDSRule,
  IDSSpecification as UISpecification,
  IDSValueField,
  isValidIdsAuthor,
  NecessityType,
  RELATIONS
} from '../models/ids.model';
import {matchEntityGroup, matchSubtypeClosure, withSubtypes} from '../models/ifc-entity-groups';
import {dataTypeGroupOf, knownDataTypes} from '../models/ifc-data-type-groups';

/**
 * Adapter-Service: Konvertiert zwischen UI-Interfaces (ids.model.ts) und IDS XML.
 *
 * - toXml():   UI IDSRule → schema-konformes IDS XML (buildingSMART IDS 1.0)
 * - fromXml(): IDS XML → UI IDSRule (DOMParser)
 */
@Injectable({providedIn: 'root'})
export class IdsAdapterService {

  // ═══════════ UI → XML ═══════════

  toXml(rule: IDSRule): string {
    let xml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
    xml += '<ids:ids xmlns:ids="http://standards.buildingsmart.org/IDS"\n';
    xml += '         xmlns:xs="http://www.w3.org/2001/XMLSchema"\n';
    xml += '         xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"\n';
    xml += '         xsi:schemaLocation="http://standards.buildingsmart.org/IDS http://standards.buildingsmart.org/IDS/1.0/ids.xsd">\n';

    xml += '    <ids:info>\n';
    xml += `        <ids:title>${this.esc(rule.info.title)}</ids:title>\n`;
    // Reihenfolge ist durch die xs:sequence im IDS-1.0-Schema vorgegeben:
    // title → copyright → version → description → author → date → purpose → milestone
    if (rule.info.copyright) xml += `        <ids:copyright>${this.esc(rule.info.copyright)}</ids:copyright>\n`;
    if (rule.info.version) xml += `        <ids:version>${this.esc(rule.info.version)}</ids:version>\n`;
    if (rule.info.description) xml += `        <ids:description>${this.esc(rule.info.description)}</ids:description>\n`;
    // <ids:author> muss laut Schema eine E-Mail-Adresse sein - ein freier Name würde die gesamte
    // IDS-Datei ungültig machen. Der Editor blockiert das Speichern in dem Fall; für alle anderen
    // Wege (z.B. Import) wird ein ungültiger Autor hier weggelassen statt die Datei zu brechen.
    if (isValidIdsAuthor(rule.info.author)) xml += `        <ids:author>${this.esc(rule.info.author!.trim())}</ids:author>\n`;
    if (rule.info.date) xml += `        <ids:date>${this.esc(rule.info.date)}</ids:date>\n`;
    if (rule.info.purpose) xml += `        <ids:purpose>${this.esc(rule.info.purpose)}</ids:purpose>\n`;
    if (rule.info.milestone) xml += `        <ids:milestone>${this.esc(rule.info.milestone)}</ids:milestone>\n`;
    xml += '    </ids:info>\n';

    xml += '    <ids:specifications>\n';
    for (const spec of rule.specifications) {
      xml += this.specToXml(spec);
    }
    xml += '    </ids:specifications>\n';
    xml += '</ids:ids>';
    return xml;
  }

  private specToXml(spec: UISpecification): string {
    // Das IDS-1.0-Schema kennt nur 'IFC2X3' | 'IFC4' | 'IFC4X3_ADD2' als gültige ifcVersion-Werte.
    // In der UI heißt die dritte Option (aus Nutzersicht) schlicht 'IFC4X3' - beim Export muss sie
    // auf den offiziellen Schema-Wert gemappt werden, sonst schlägt die XSD-Validierung fehl.
    const ifcVersion = spec.ifcVersions.map(v => this.toSchemaIfcVersion(v)).join(' ');
    let xml = `        <ids:specification ifcVersion="${this.esc(ifcVersion)}" name="${this.esc(spec.name)}"`;
    if (spec.description) xml += ` description="${this.esc(spec.description)}"`;
    if (spec.instruction) xml += ` instructions="${this.esc(spec.instruction)}"`;
    if (spec.identifier) xml += ` identifier="${this.esc(spec.identifier)}"`;
    xml += '>\n';
    const minOccurs = spec.necessity === 'required' ? '1' : '0';
    const maxOccurs = spec.necessity === 'prohibited' ? '0' : 'unbounded';
    xml += `            <ids:applicability minOccurs="${minOccurs}" maxOccurs="${maxOccurs}">\n`;
    for (const facet of spec.applicability) {
      xml += this.facetToXml(facet, 16, false, spec.ifcVersions);
    }
    xml += '            </ids:applicability>\n';
    xml += '            <ids:requirements>\n';
    for (const facet of spec.requirements) {
      xml += this.facetToXml(facet, 16, true, spec.ifcVersions);
    }
    xml += '            </ids:requirements>\n';
    xml += '        </ids:specification>\n';
    return xml;
  }

  /**
   * Der zu schreibende Entitätsname — bei „Subklassen mitprüfen" (#2311) um den Subtyp-Abschluss
   * erweitert.
   *
   * <p>Nötig, weil `ifctester` Entitäten exakt vergleicht (`Entity.filter` mit
   * `include_subtypes=False`): eine Spezifikation auf `IfcWall` erfasst sonst keine
   * `IfcWallStandardCase`. Im XML steht deshalb die ausgeschriebene Aufzählung — damit bleibt die
   * Regel für Fremdwerkzeuge lesbar, ohne BIC-eigene Erweiterung.
   *
   * <p>Gibt bewusst ein <b>neues</b> Feld zurück und ändert das Facet nicht: sonst stünde im
   * Editor plötzlich die Expansion statt der eingetippten Klasse, und die Regel gälte allein durch
   * das Speichern als geändert.
   *
   * <p>Nur „Wert" und „Aufzählung" lassen sich expandieren. Ein Muster oder eine Längen-Grenze
   * nennt keine Klassen, aus denen ein Abschluss zu bilden wäre — dort bleibt das Feld unberührt.
   */
  private expandedEntityName(facet: UIFacet, ifcVersions: string[]): IDSValueField {
    const field = facet.entityName;
    if (!facet.includeSubtypes) return field;

    const parents = field.dataType === 'Wert'
      ? [field.simpleValue ?? '']
      : field.dataType === 'Aufzählung'
        ? (field.enumValues ?? [])
        : [];
    const named = parents.map(name => (name ?? '').trim()).filter(name => name.length > 0);
    if (named.length === 0) return field;

    const expanded = new Set<string>();
    for (const parent of named) {
      for (const name of withSubtypes(parent, ifcVersions)) expanded.add(name);
    }
    // Keine Subklassen gefunden (Blattklasse oder unbekannter Name): das Feld unverändert lassen,
    // statt eine Aufzählung mit genau einem Eintrag zu erzeugen.
    if (expanded.size <= named.length) return field;

    return {dataType: 'Aufzählung', enumValues: [...expanded].sort()};
  }

  /**
   * Erkennt Entitätengruppen (#2310) und „Subklassen mitprüfen" (#2311) in einer geladenen
   * Spezifikation wieder.
   *
   * <p>Im XML steht in beiden Fällen nur eine Aufzählung — der Editor soll aber die Gruppe bzw.
   * die eingetippten Elternklassen zeigen und nicht deren Expansion. Erkannt wird am
   * Mengenvergleich, nicht an einem Marker: so wird auch eine von außen importierte IDS richtig
   * dargestellt.
   *
   * <p>Die Gruppe hat Vorrang. Sie ist selbst ein Subtyp-Abschluss (etwa „Alle Elemente" = der
   * Abschluss von `IfcProduct`) und würde sonst als Elternklasse plus Häkchen dargestellt — die
   * Gruppe ist die aussagekräftigere Lesart. In der Anwendbarkeit wird sie dabei als eigener
   * Facettentyp `entityGroup` eingestuft (dort exklusiv wählbar); in Anforderungen bleibt es bei
   * `entity`.
   */
  private collapseEntitySubtypes(spec: UISpecification): void {
    this.collapseEntitySubtypesIn(spec.applicability, spec.ifcVersions, true);
    this.collapseEntitySubtypesIn(spec.requirements, spec.ifcVersions, false);
  }

  private collapseEntitySubtypesIn(facets: UIFacet[], ifcVersions: string[], isApplicability: boolean): void {
    for (const facet of facets) {
      if (facet.type !== 'entity') continue;
      const field = facet.entityName;
      if (field.dataType !== 'Aufzählung') continue;

      const values = field.enumValues ?? [];
      if (matchEntityGroup(values, ifcVersions)) {
        if (isApplicability) facet.type = 'entityGroup';
        continue;
      }

      const parents = matchSubtypeClosure(values, ifcVersions);
      if (!parents) continue;

      facet.includeSubtypes = true;
      if (parents.length === 1) {
        // Eine einzelne Elternklasse liest sich als Einzelwert deutlich besser als als
        // Aufzählung mit einem Eintrag.
        facet.entityName = {dataType: 'Wert', simpleValue: parents[0], enumValues: []};
      } else {
        field.enumValues = parents;
      }
    }
  }

  private facetToXml(facet: UIFacet, indent: number, isReq: boolean, ifcVersions: string[] = []): string {
    const pad = ' '.repeat(indent);
    let xml = '';
    switch (facet.type) {
      case 'entity':
      case 'entityGroup': {
        // Anders als bei den übrigen Facettentypen erlaubt das IDS-1.0-Schema bei <ids:entity>
        // innerhalb von <ids:requirements> KEIN cardinality-Attribut (nur instructions) - die
        // entityType-Erweiterung des Schemas kennt kein cardinality. Ein cardinality-Attribut hier
        // lässt den Server die gesamte IDS-Datei mit einem XSD-Fehler ablehnen (nicht nur diese
        // eine Spezifikation). Die Necessity einer Entity-Anforderung kann deshalb nicht exportiert
        // werden; der Editor fixiert sie passend dazu auf "Erforderlich" (siehe
        // IdsFacetComponent.necessityLockedToRequired).
        const a = this.entityReqAttrs(facet, isReq);
        xml += `${pad}<ids:entity${a}>\n`;
        xml += this.vfToXml(this.expandedEntityName(facet, ifcVersions), 'ids:name', indent + 4);
        if (this.hasVal(facet.predefinedType)) xml += this.vfToXml(facet.predefinedType, 'ids:predefinedType', indent + 4);
        xml += `${pad}</ids:entity>\n`;
        break;
      }
      case 'attribute': {
        let a = this.reqAttrs(facet, isReq);
        xml += `${pad}<ids:attribute${a}>\n`;
        xml += this.vfToXml(facet.attributeName, 'ids:name', indent + 4);
        if (this.hasVal(facet.attributeValue)) xml += this.vfToXml(facet.attributeValue, 'ids:value', indent + 4);
        xml += `${pad}</ids:attribute>\n`;
        break;
      }
      case 'property': {
        let a = '';
        if (facet.propertyDataType) a += ` dataType="${this.esc(facet.propertyDataType.toUpperCase())}"`;
        a += this.reqAttrs(facet, isReq);
        xml += `${pad}<ids:property${a}>\n`;
        xml += this.vfToXmlRequired(facet.propertySet, 'ids:propertySet', indent + 4);
        xml += this.vfToXml(facet.propertyName, 'ids:baseName', indent + 4);
        if (this.hasVal(facet.propertyValue)) xml += this.vfToXml(facet.propertyValue, 'ids:value', indent + 4);
        xml += `${pad}</ids:property>\n`;
        break;
      }
      case 'classification': {
        let a = this.reqAttrs(facet, isReq);
        xml += `${pad}<ids:classification${a}>\n`;
        if (this.hasVal(facet.classificationValue)) xml += this.vfToXml(facet.classificationValue, 'ids:value', indent + 4);
        if (this.hasVal(facet.classificationSystem)) xml += this.vfToXml(facet.classificationSystem, 'ids:system', indent + 4);
        xml += `${pad}</ids:classification>\n`;
        break;
      }
      case 'material': {
        let a = this.reqAttrs(facet, isReq);
        if (this.hasVal(facet.materialName)) {
          xml += `${pad}<ids:material${a}>\n`;
          xml += this.vfToXml(facet.materialName, 'ids:value', indent + 4);
          xml += `${pad}</ids:material>\n`;
        } else {
          xml += `${pad}<ids:material${a} />\n`;
        }
        break;
      }
      case 'partOf': {
        let a = '';
        // Schema-Enumeration verlangt Grossschreibung (z.B. "IFCRELAGGREGATES"), die UI zeigt
        // die lesbare IFC-Schreibweise ("IfcRelAggregates") im Dropdown.
        if (facet.relation) a += ` relation="${this.esc(facet.relation.toUpperCase())}"`;
        a += this.reqAttrs(facet, isReq);
        xml += `${pad}<ids:partOf${a}>\n`;
        xml += `${pad}    <ids:entity>\n`;
        xml += this.vfToXml(facet.partOfEntityName, 'ids:name', indent + 8);
        if (this.hasVal(facet.partOfPredefinedType)) xml += this.vfToXml(facet.partOfPredefinedType, 'ids:predefinedType', indent + 8);
        xml += `${pad}    </ids:entity>\n`;
        xml += `${pad}</ids:partOf>\n`;
        break;
      }
    }
    return xml;
  }

  private reqAttrs(facet: UIFacet, isReq: boolean): string {
    if (!isReq) return '';
    let a = '';
    if (facet.metaNecessity) a += ` cardinality="${facet.metaNecessity}"`;
    if (facet.metaInstruction) a += ` instructions="${this.esc(facet.metaInstruction)}"`;
    if (facet.metaDescription) a += ` description="${this.esc(facet.metaDescription)}"`;
    if (facet.metaUri) a += ` uri="${this.esc(facet.metaUri)}"`;
    return a;
  }

  /**
   * Attribute für <ids:entity> in Anforderungen: die entityType-Erweiterung des IDS-1.0-Schemas
   * erlaubt dort ausschließlich `instructions`, kein `cardinality`/`description`/`uri` (siehe
   * Kommentar in facetToXml). Die Necessity lässt sich hier also nicht abbilden - der Editor
   * fixiert sie stattdessen auf "Erforderlich", statt eine Einstellung anzubieten, die beim Export
   * ohnehin verloren geht bzw. den Server-Import brechen würde.
   */
  private entityReqAttrs(facet: UIFacet, isReq: boolean): string {
    if (!isReq) return '';
    return facet.metaInstruction ? ` instructions="${this.esc(facet.metaInstruction)}"` : '';
  }

  /**
   * Wie vfToXml, aber mit Wildcard-Fallback: fehlt ein Wert, matcht ifctester per
   * `xs:pattern value=".*"` jedes beliebige Property-Set, statt implizit "kein Pset" zu verlangen
   * (ein leeres `<ids:propertySet><simpleValue></simpleValue></ids:propertySet>` würde nach einem
   * Pset mit dem Namen "" suchen, das es nie gibt).
   *
   * <p>Bewusst NUR für `propertySet` verwendet, nicht für `baseName`/propertyName: ein leerer
   * Eigenschaftsname ist laut `isFacetInvalid()` ein echter Validierungsfehler, der das Speichern
   * blockiert. Würde auch dort der Wildcard geschrieben, käme beim erneuten Laden ein Datentyp
   * "Muster" mit Pattern ".*" zurück - die Anforderung sähe im Editor fälschlich vollständig
   * ausgefüllt aus, obwohl der Eigenschaftsname nie gesetzt wurde.
   */
  private vfToXmlRequired(field: IDSValueField, tag: string, indent: number): string {
    const result = this.vfToXml(field, tag, indent);
    if (result) return result;
    // Fallback: use wildcard pattern so ifctester matches any property set / property name
    const pad = ' '.repeat(indent);
    return `${pad}<${tag}>\n${pad}    <xs:restriction base="xs:string">\n${pad}        <xs:pattern value=".*" />\n${pad}    </xs:restriction>\n${pad}</${tag}>\n`;
  }

  private vfToXml(field: IDSValueField, tag: string, indent: number): string {
    const pad = ' '.repeat(indent);
    switch (field.dataType) {
      case 'Aufzählung':
        if (!field.enumValues?.length) return '';
        return `${pad}<${tag}>\n${pad}    <xs:restriction base="xs:string">\n${field.enumValues.map(v => `${pad}        <xs:enumeration value="${this.esc(v)}" />\n`).join('')}${pad}    </xs:restriction>\n${pad}</${tag}>\n`;
      case 'Grenzen':
        return `${pad}<${tag}>\n${pad}    <xs:restriction base="xs:string">\n${field.rangeMin ? `${pad}        <xs:minInclusive value="${this.esc(field.rangeMin)}" />\n` : ''}${field.rangeMax ? `${pad}        <xs:maxInclusive value="${this.esc(field.rangeMax)}" />\n` : ''}${pad}    </xs:restriction>\n${pad}</${tag}>\n`;
      case 'Muster':
        if (!field.pattern) return '';
        return `${pad}<${tag}>\n${pad}    <xs:restriction base="xs:string">\n${pad}        <xs:pattern value="${this.esc(field.pattern)}" />\n${pad}    </xs:restriction>\n${pad}</${tag}>\n`;
      case 'Länge':
        if (!field.lengthValue) return '';
        return `${pad}<${tag}>\n${pad}    <xs:restriction base="xs:string">\n${pad}        <xs:length value="${this.esc(field.lengthValue)}" />\n${pad}    </xs:restriction>\n${pad}</${tag}>\n`;
      case 'Länge Grenzen':
        if (!field.lengthMin && !field.lengthMax) return '';
        return `${pad}<${tag}>\n${pad}    <xs:restriction base="xs:string">\n${field.lengthMin ? `${pad}        <xs:minLength value="${this.esc(field.lengthMin)}" />\n` : ''}${field.lengthMax ? `${pad}        <xs:maxLength value="${this.esc(field.lengthMax)}" />\n` : ''}${pad}    </xs:restriction>\n${pad}</${tag}>\n`;
      case 'Wert':
      default:
        if (!field.simpleValue) return '';
        return `${pad}<${tag}>\n${pad}    <ids:simpleValue>${this.esc(field.simpleValue)}</ids:simpleValue>\n${pad}</${tag}>\n`;
    }
  }

  /**
   * Filtert eine IDS-XML auf die angegebenen Spezifikationsnamen. Wird für die manuelle
   * Teilauswahl einzelner "Regeln" (= Spezifikationen) innerhalb eines IDS-Regelsets bei der
   * myCheck-Ausführung benötigt: eine IDS-Spezifikation existiert nur eingebettet in der einen
   * XML-Datei des gesamten Regelsets, nicht als eigenständige Datenbank-Regel - daher muss vor
   * der Ausführung eine reduzierte XML mit nur den ausgewählten Spezifikationen gebaut werden.
   */
  filterSpecificationsByName(xml: string, specNames: string[]): string {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    const specsEl = this.byLocal(doc.documentElement, 'specifications');
    if (!specsEl) return xml;

    const wanted = new Set(specNames);
    const toRemove = this.allByLocal(specsEl, 'specification')
      .filter(el => !wanted.has(el.getAttribute('name') ?? ''));
    toRemove.forEach(el => specsEl.removeChild(el));

    return new XMLSerializer().serializeToString(doc);
  }

  // ═══════════ XML → UI ═══════════

  fromXml(xml: string): IDSRule {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    return {
      info: this.parseInfo(doc),
      specifications: this.parseSpecs(doc)
    };
  }

  private parseInfo(doc: Document): UIInfo {
    const info = this.byLocal(doc.documentElement, 'info');
    return {
      title: this.txt(info, 'title') ?? '',
      copyright: this.txt(info, 'copyright'),
      version: this.txt(info, 'version'),
      description: this.txt(info, 'description'),
      author: this.txt(info, 'author'),
      date: this.txt(info, 'date'),
      purpose: this.txt(info, 'purpose'),
      milestone: this.txt(info, 'milestone'),
    };
  }

  private parseSpecs(doc: Document): UISpecification[] {
    const specsEl = this.byLocal(doc.documentElement, 'specifications');
    if (!specsEl) return [];
    return this.allByLocal(specsEl, 'specification').map(el => this.parseSpec(el));
  }

  private parseSpec(el: Element): UISpecification {
    const spec = createDefaultSpecification();
    spec.name = el.getAttribute('name') ?? '';
    spec.description = el.getAttribute('description') ?? undefined;
    spec.instruction = el.getAttribute('instructions') ?? undefined;
    spec.identifier = el.getAttribute('identifier') ?? undefined;
    spec.ifcVersions = (el.getAttribute('ifcVersion') ?? '').split(/\s+/)
      .filter(v => v.length > 0)
      .map(v => this.fromSchemaIfcVersion(v));

    const appEl = this.byLocal(el, 'applicability');
    if (appEl) {
      const minOccurs = appEl.getAttribute('minOccurs');
      const maxOccurs = appEl.getAttribute('maxOccurs');
      if (minOccurs === '0' && maxOccurs === '0') spec.necessity = 'prohibited';
      else if (minOccurs === '0') spec.necessity = 'optional';
      else spec.necessity = 'required';
    }
    spec.applicability = appEl ? this.parseFacets(appEl, false) : [];

    const reqEl = this.byLocal(el, 'requirements');
    spec.requirements = reqEl ? this.parseFacets(reqEl, true) : [];

    // Erst hier, weil dafür die IFC-Versionen der Spezifikation und alle Facetten vorliegen
    // müssen. Bewusst als Nachbearbeitung, statt die Versionen durch alle sechs
    // Facetten-Parser zu fädeln.
    this.collapseEntitySubtypes(spec);

    return spec;
  }

  private parseFacets(parent: Element, isReq: boolean): UIFacet[] {
    const facets: UIFacet[] = [];
    for (const child of Array.from(parent.children)) {
      const ln = child.localName;
      if (ln === 'entity') facets.push(this.pEntity(child, isReq));
      else if (ln === 'attribute') facets.push(this.pAttribute(child, isReq));
      else if (ln === 'property') facets.push(this.pProperty(child, isReq));
      else if (ln === 'classification') facets.push(this.pClassification(child, isReq));
      else if (ln === 'material') facets.push(this.pMaterial(child, isReq));
      else if (ln === 'partOf') facets.push(this.pPartOf(child, isReq));
    }
    return facets;
  }

  private pEntity(el: Element, isReq: boolean): UIFacet {
    const f = createDefaultFacet('entity');
    // Standard: <name> + <predefinedType>
    const ptEl = this.byLocal(el, 'predefinedType');
    const nameEls = this.allByLocal(el, 'name');

    if (nameEls.length > 0) f.entityName = this.pValueField(nameEls[0]);

    if (ptEl) {
      // Standard IDS: <predefinedType> element
      f.predefinedType = this.pValueField(ptEl);
    } else if (nameEls.length > 1) {
      // thatopen bug workaround: predefinedType was exported as second <name>
      f.predefinedType = this.pValueField(nameEls[1]);
    }

    if (isReq) this.applyCard(el, f);
    return f;
  }

  private pAttribute(el: Element, isReq: boolean): UIFacet {
    const f = createDefaultFacet('attribute');
    const n = this.byLocal(el, 'name'); if (n) f.attributeName = this.pValueField(n);
    const v = this.byLocal(el, 'value'); if (v) f.attributeValue = this.pValueField(v);
    if (isReq) this.applyCard(el, f);
    return f;
  }

  private pProperty(el: Element, isReq: boolean): UIFacet {
    const f = createDefaultFacet('property');
    const dt = el.getAttribute('dataType');
    if (dt) {
      // Ein Datentypengruppen-Token (#2312) wird auf den lesbaren Gruppennamen zurückgeführt (im
      // XML steht groß, im Editor soll "Text" stehen, nicht "TEXT" - facetToXml schreibt beim
      // Speichern wieder groß). Ein echter IFC-Typ wird auf seine korrekte Schreibweise
      // zurückgeführt, sonst bleibt der Wert unverändert — eine fremde IDS darf keinen Datentyp
      // verlieren, nur weil wir ihn nicht kennen.
      const group = dataTypeGroupOf(dt);
      const proper = group
        ? group.label
        : knownDataTypes().find(name => name.toUpperCase() === dt.toUpperCase());
      f.propertyDataType = proper ?? dt;
    }
    const ps = this.byLocal(el, 'propertySet');
    if (ps) {
      const parsed = this.pValueField(ps);
      // xs:pattern value=".*" ist der XML-Wildcard-Fallback — intern als {Wert, '.*'} speichern
      f.propertySet = (parsed.dataType === 'Muster' && parsed.pattern === '.*')
        ? { ...createDefaultValueField(), simpleValue: '.*' }
        : parsed;
    }
    const bn = this.byLocal(el, 'baseName'); if (bn) f.propertyName = this.pValueField(bn);
    const v = this.byLocal(el, 'value'); if (v) f.propertyValue = this.pValueField(v);
    if (isReq) this.applyCard(el, f);
    return f;
  }

  private pClassification(el: Element, isReq: boolean): UIFacet {
    const f = createDefaultFacet('classification');
    const v = this.byLocal(el, 'value'); if (v) f.classificationValue = this.pValueField(v);
    const s = this.byLocal(el, 'system'); if (s) f.classificationSystem = this.pValueField(s);
    if (isReq) this.applyCard(el, f);
    return f;
  }

  private pMaterial(el: Element, isReq: boolean): UIFacet {
    const f = createDefaultFacet('material');
    const v = this.byLocal(el, 'value'); if (v) f.materialName = this.pValueField(v);
    if (isReq) this.applyCard(el, f);
    return f;
  }

  private pPartOf(el: Element, isReq: boolean): UIFacet {
    const f = createDefaultFacet('partOf');
    const rel = el.getAttribute('relation'); if (rel) f.relation = this.fromSchemaRelation(rel);
    const ent = this.byLocal(el, 'entity');
    if (ent) {
      const n = this.byLocal(ent, 'name'); if (n) f.partOfEntityName = this.pValueField(n);
      const p = this.byLocal(ent, 'predefinedType'); if (p) f.partOfPredefinedType = this.pValueField(p);
    }
    if (isReq) this.applyCard(el, f);
    return f;
  }

  private applyCard(el: Element, facet: UIFacet) {
    const c = el.getAttribute('cardinality');
    facet.metaNecessity = (c === 'optional' || c === 'prohibited') ? c as NecessityType : 'required';
    const inst = el.getAttribute('instructions'); if (inst) facet.metaInstruction = inst;
    const desc = el.getAttribute('description'); if (desc) facet.metaDescription = desc;
    const uri = el.getAttribute('uri'); if (uri) facet.metaUri = uri;
  }

  private pValueField(el: Element): IDSValueField {
    const sv = this.byLocal(el, 'simpleValue');
    if (sv) return {...createDefaultValueField(), dataType: 'Wert', simpleValue: sv.textContent ?? ''};
    const rest = this.byLocal(el, 'restriction');
    if (rest) {
      const enums = this.allByLocal(rest, 'enumeration');
      if (enums.length) return {...createDefaultValueField(), dataType: 'Aufzählung', enumValues: enums.map(e => e.getAttribute('value') ?? '')};
      const pat = this.byLocal(rest, 'pattern');
      if (pat) return {...createDefaultValueField(), dataType: 'Muster', pattern: pat.getAttribute('value') ?? ''};
      // Exact length (xs:length)
      const len = this.byLocal(rest, 'length');
      if (len) return {...createDefaultValueField(), dataType: 'Länge', lengthValue: len.getAttribute('value') ?? ''};
      // Length bounds (xs:minLength / xs:maxLength)
      const minLen = this.byLocal(rest, 'minLength'), maxLen = this.byLocal(rest, 'maxLength');
      if (minLen || maxLen) return {...createDefaultValueField(), dataType: 'Länge Grenzen', lengthMin: minLen?.getAttribute('value') ?? undefined, lengthMax: maxLen?.getAttribute('value') ?? undefined};
      // Value bounds (xs:minInclusive / xs:maxInclusive)
      const mn = this.byLocal(rest, 'minInclusive'), mx = this.byLocal(rest, 'maxInclusive');
      if (mn || mx) return {...createDefaultValueField(), dataType: 'Grenzen', rangeMin: mn?.getAttribute('value') ?? undefined, rangeMax: mx?.getAttribute('value') ?? undefined};
    }
    return createDefaultValueField();
  }

  // ═══════════ Hilfsfunktionen ═══════════

  private hasVal(field: IDSValueField): boolean {
    switch (field.dataType) {
      case 'Aufzählung': return (field.enumValues?.length ?? 0) > 0;
      case 'Grenzen': return !!(field.rangeMin || field.rangeMax);
      case 'Muster': return !!field.pattern;
      case 'Länge': return !!field.lengthValue;
      case 'Länge Grenzen': return !!(field.lengthMin || field.lengthMax);
      default: return !!field.simpleValue;
    }
  }

  private byLocal(parent: Element | null, localName: string): Element | null {
    if (!parent) return null;
    // Try children first (works for most browsers)
    for (let i = 0; i < parent.childNodes.length; i++) {
      const child = parent.childNodes[i];
      if (child.nodeType === 1) { // ELEMENT_NODE
        const el = child as Element;
        if (el.localName === localName || el.tagName === localName || el.tagName.endsWith(':' + localName)) {
          return el;
        }
      }
    }
    // Fallback: getElementsByTagNameNS with wildcard namespace
    const results = parent.getElementsByTagNameNS('*', localName);
    for (let i = 0; i < results.length; i++) {
      if (results[i].parentNode === parent) return results[i];
    }
    return null;
  }

  private allByLocal(parent: Element, localName: string): Element[] {
    const result: Element[] = [];
    for (let i = 0; i < parent.childNodes.length; i++) {
      const child = parent.childNodes[i];
      if (child.nodeType === 1) {
        const el = child as Element;
        if (el.localName === localName || el.tagName === localName || el.tagName.endsWith(':' + localName)) {
          result.push(el);
        }
      }
    }
    if (result.length === 0) {
      // Fallback: getElementsByTagNameNS
      const results = parent.getElementsByTagNameNS('*', localName);
      for (let i = 0; i < results.length; i++) {
        if (results[i].parentNode === parent) result.push(results[i]);
      }
    }
    return result;
  }

  private txt(parent: Element | null, localName: string): string | undefined {
    return this.byLocal(parent, localName)?.textContent ?? undefined;
  }

  private esc(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /** UI-Wert 'IFC4X3' -> offizieller IDS-1.0-Schema-Wert 'IFC4X3_ADD2' (einzig gültiger Enum-Wert). */
  private toSchemaIfcVersion(uiVersion: string): string {
    return uiVersion === 'IFC4X3' ? 'IFC4X3_ADD2' : uiVersion;
  }

  /** Schema-Wert 'IFC4X3_ADD2' -> UI-Wert 'IFC4X3' (Gegenstück zu toSchemaIfcVersion). */
  private fromSchemaIfcVersion(schemaVersion: string): string {
    return schemaVersion === 'IFC4X3_ADD2' ? 'IFC4X3' : schemaVersion;
  }

  /** Schema-Wert 'IFCRELAGGREGATES' -> UI-Anzeige 'IfcRelAggregates' (Gegenstück zum Uppercase in facetToXml). */
  private fromSchemaRelation(schemaRelation: string): string {
    return RELATIONS.find(r => r.toUpperCase() === schemaRelation.toUpperCase()) ?? schemaRelation;
  }
}
